import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash } from "lucide-react";
import { toast } from "sonner";
import AIQuestionGenerator from "./AIQuestionGenerator";
import EnhancedQuestionEditor from "./EnhancedQuestionEditor";
import EnhancedManualQuestionAdder from "./EnhancedManualQuestionAdder";

interface TestQuestion {
  id: string;
  question_order: number;
  points: number;
  custom_question_data?: any;
  question_id?: string;
  question?: {
    id: string;
    text: string;
    explanation?: string;
    options: Array<{
      id: string;
      text: string;
      is_correct: boolean;
    }>;
  };
}

interface InstructorQuestionManagerProps {
  testId: string;
  testData: {
    subject: string;
    grade: string;
    topic: string;
  };
  onQuestionsUpdated: () => void;
}

export default function InstructorQuestionManager({ testId, testData, onQuestionsUpdated }: InstructorQuestionManagerProps) {
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState<TestQuestion | null>(null);

  useEffect(() => {
    loadQuestions();
  }, [testId]);

  const loadQuestions = async () => {
    try {
      const { data, error } = await supabase
        .from('instructor_test_questions')
        .select(`
          id,
          question_order,
          points,
          custom_question_data,
          question_id,
          questions:question_id (
            id,
            text,
            explanation,
            options (
              id,
              text,
              is_correct
            )
          )
        `)
        .eq('test_id', testId)
        .order('question_order');

      if (error) throw error;
      setQuestions(data || []);
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      const { error } = await supabase
        .from('instructor_test_questions')
        .delete()
        .eq('id', questionId);

      if (error) throw error;

      // Update the test's total_questions count
      await supabase
        .from('instructor_tests')
        .update({ total_questions: questions.length - 1 })
        .eq('id', testId);

      toast.success('Question deleted successfully');
      loadQuestions();
      onQuestionsUpdated();
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error('Failed to delete question');
    }
  };

  const renderQuestion = (testQuestion: TestQuestion, index: number) => {
    const questionData = testQuestion.custom_question_data || testQuestion.question;
    if (!questionData) return null;

    return (
      <Card key={testQuestion.id}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-base">
                Question {index + 1}
                <Badge variant="outline" className="ml-2">
                  {testQuestion.points} point{testQuestion.points !== 1 ? 's' : ''}
                </Badge>
              </CardTitle>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingQuestion(testQuestion)}
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleDeleteQuestion(testQuestion.id)}
              >
                <Trash className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm font-medium">{questionData.text}</p>
            
            {questionData.options && (
              <div className="space-y-2">
                {questionData.options.map((option: any, optIndex: number) => (
                  <div 
                    key={optIndex}
                    className={`p-2 rounded text-xs border ${
                      option.is_correct 
                        ? 'border-green-200 bg-green-50 text-green-800' 
                        : 'border-gray-200'
                    }`}
                  >
                    {String.fromCharCode(65 + optIndex)}. {option.text}
                    {option.is_correct && <span className="ml-1 font-medium">(Correct)</span>}
                  </div>
                ))}
              </div>
            )}
            
            {questionData.explanation && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  <strong>Explanation:</strong> {questionData.explanation}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div>Loading questions...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Questions ({questions.length})</h3>
        <div className="flex gap-2">
          <AIQuestionGenerator 
            testId={testId}
            testData={testData}
            onQuestionsGenerated={loadQuestions}
          />
          <EnhancedManualQuestionAdder
            testId={testId}
            testData={testData}
            onQuestionAdded={loadQuestions}
          />
        </div>
      </div>

      {questions.length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              No questions added yet. Add your first question to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {questions.map((question, index) => renderQuestion(question, index))}
        </div>
      )}

      {/* Enhanced Question Editor */}
      {editingQuestion && (
        <EnhancedQuestionEditor
          question={{
            id: editingQuestion.id,
            text: editingQuestion.custom_question_data?.text || editingQuestion.question?.text || '',
            type: editingQuestion.custom_question_data?.type || 'multiple_choice',
            explanation: editingQuestion.custom_question_data?.explanation || editingQuestion.question?.explanation,
            difficulty: editingQuestion.custom_question_data?.difficulty || 'medium',
            options: editingQuestion.custom_question_data?.options || editingQuestion.question?.options,
            pairs: editingQuestion.custom_question_data?.pairs,
            puzzle_data: editingQuestion.custom_question_data?.puzzle_data,
            blanks: editingQuestion.custom_question_data?.blanks,
            correct_answer: editingQuestion.custom_question_data?.correct_answer
          }}
          testData={testData}
          isOpen={!!editingQuestion}
          onSave={async (questionData) => {
            try {
              const { error } = await supabase
                .from('instructor_test_questions')
                .update({
                  custom_question_data: {
                    text: questionData.text,
                    type: questionData.type,
                    explanation: questionData.explanation,
                    difficulty: questionData.difficulty,
                    options: questionData.options,
                    pairs: questionData.pairs,
                    puzzle_data: questionData.puzzle_data,
                    blanks: questionData.blanks,
                    correct_answer: questionData.correct_answer
                  },
                  question_type: questionData.type as any
                })
                .eq('id', editingQuestion.id);

              if (error) throw error;

              toast.success('Question updated successfully!');
              setEditingQuestion(null);
              loadQuestions();
              onQuestionsUpdated();
            } catch (error) {
              console.error('Error updating question:', error);
              toast.error('Failed to update question');
            }
          }}
          onCancel={() => setEditingQuestion(null)}
        />
      )}
    </div>
  );
}